import type { TypographyOptions } from '@mui/material/styles/createTypography';

import { setFont, pxToRem, responsiveFontSizes } from '../styles/utils';

// ----------------------------------------------------------------------

declare module '@mui/material/styles' {
  interface TypographyVariants {
    fontSecondaryFamily: React.CSSProperties['fontFamily'];
    fontWeightSemiBold: React.CSSProperties['fontWeight'];
  }
  interface TypographyVariantsOptions {
    fontSecondaryFamily?: React.CSSProperties['fontFamily'];
    fontWeightSemiBold?: React.CSSProperties['fontWeight'];
  }
  interface ThemeVars {
    typography: Theme['typography'];
  }
}

// ----------------------------------------------------------------------

export const defaultFont = 'Manrope Variable';

export const primaryFont = setFont(defaultFont);

export const secondaryFont = setFont('Barlow');

// ----------------------------------------------------------------------

export const typography: TypographyOptions = {
  fontFamily: primaryFont,
  fontSecondaryFamily: secondaryFont,
  fontWeightLight: '300',
  fontWeightRegular: '400',
  fontWeightMedium: '500',
  fontWeightSemiBold: '600',
  fontWeightBold: '700',
  h1: {
    fontWeight: 800,
    lineHeight: 1.2,
    fontSize: pxToRem(34),
    fontFamily: primaryFont,
    letterSpacing: 0,
    ...responsiveFontSizes({ sm: 38, md: 44, lg: 48 }),
  },
  h2: {
    fontWeight: 800,
    lineHeight: 1.25,
    fontSize: pxToRem(28),
    fontFamily: primaryFont,
    letterSpacing: 0,
    ...responsiveFontSizes({ sm: 32, md: 36, lg: 40 }),
  },
  h3: {
    fontWeight: 700,
    lineHeight: 1.3,
    fontSize: pxToRem(24),
    fontFamily: primaryFont,
    letterSpacing: 0,
    ...responsiveFontSizes({ sm: 25, md: 28, lg: 32 }),
  },
  h4: {
    fontWeight: 700,
    lineHeight: 1.35,
    fontSize: pxToRem(20),
    letterSpacing: 0,
    ...responsiveFontSizes({ sm: 22, md: 24, lg: 24 }),
  },
  h5: {
    fontWeight: 700,
    lineHeight: 1.4,
    fontSize: pxToRem(18),
    letterSpacing: 0,
    ...responsiveFontSizes({ sm: 19, md: 20, lg: 20 }),
  },
  h6: {
    fontWeight: 600,
    lineHeight: 1.45,
    fontSize: pxToRem(16),
    letterSpacing: 0,
    ...responsiveFontSizes({ sm: 18, md: 18, lg: 18 }),
  },
  subtitle1: {
    fontWeight: 600,
    lineHeight: 24 / 15,
    fontSize: pxToRem(15),
    letterSpacing: 0,
    ...responsiveFontSizes({ sm: 16, md: 16, lg: 16 }),
  },
  subtitle2: {
    fontWeight: 600,
    lineHeight: 22 / 14,
    fontSize: pxToRem(14),
    letterSpacing: 0,
  },
  body1: {
    lineHeight: 24 / 15,
    fontSize: pxToRem(15),
    letterSpacing: 0,
    ...responsiveFontSizes({ sm: 16, md: 16, lg: 16 }),
  },
  body2: {
    lineHeight: 22 / 14,
    fontSize: pxToRem(14),
    letterSpacing: 0,
  },
  caption: {
    lineHeight: 1.5,
    fontSize: pxToRem(12),
    letterSpacing: 0,
  },
  overline: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(11),
    letterSpacing: 0,
    textTransform: 'uppercase',
    ...responsiveFontSizes({ sm: 12, md: 12, lg: 12 }),
  },
  button: {
    fontWeight: 600,
    lineHeight: 20 / 14,
    fontSize: pxToRem(14),
    letterSpacing: 0,
    textTransform: 'unset',
  },
};
